import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export const ConvoyVisualization = ({ specs }) => {
  const chartRef = useRef(null);
  const { length, clearance, axleSpacing } = specs;

  // Generate visualization data
  const generateVisualizationData = () => {
    // Generate points for vehicle outline
    const points = [];
    const step = 0.1; // 10cm resolution
    
    const rearAxle = axleSpacing;
    const rearHeight = specs.rearHeight || clearance;
    
    // Generate vehicle profile
    for (let x = 0; x <= length; x += step) {
      let y;
      
      // Front point - start at ground
      if (Math.abs(x) < 0.1) {
        y = 0;
      }
      // Immediate rise to load clearance after front point
      else if (x <= rearAxle) {
        y = clearance;
      }
      // Rear axle contact area
      else if (x > rearAxle && x <= rearAxle + specs.rearContact) {
        y = 0; // Rear axle contact
      }
      // End of rear contact - back to load clearance
      else if (x > rearAxle + specs.rearContact && x < length - 0.1) {
        y = clearance;
      }
      // Last point - use rear height
      else {
        y = rearHeight;
      }
      
      points.push({ x, y });
    }

    return points;
  };

  const data = {
    datasets: [
      {
        label: 'Vehicle Profile',
        data: generateVisualizationData(),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#9CA3AF'
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `Height: ${context.parsed.y.toFixed(2)}m`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        min: -0.2, // Minimal padding before front
        max: length + 1,
        title: {
          display: true,
          text: 'Distance (m)',
          color: '#9CA3AF'
        },
        grid: {
          color: '#374151'
        },
        ticks: {
          color: '#9CA3AF'
        }
      },
      y: {
        min: -clearance/8, // Minimal padding below ground
        max: clearance * 1.5,
        title: {
          display: true,
          text: 'Height (m)',
          color: '#9CA3AF'
        },
        grid: {
          color: '#374151'
        },
        ticks: {
          color: '#9CA3AF'
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="h-64 bg-gray-800 rounded-lg p-2">
        <Line ref={chartRef} data={data} options={options} />
      </div>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="bg-gray-700/50 p-3 rounded-lg">
          <div className="text-gray-300">Vehicle Dimensions:</div>
          <div className="space-y-1 mt-1">
            <div className="flex justify-between">
              <span className="text-gray-400">Total Length:</span>
              <span className="text-white">{length}m</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Axle Spacing:</span>
              <span className="text-white">{axleSpacing}m</span>
            </div>
          </div>
        </div>
        <div className="bg-gray-700/50 p-3 rounded-lg">
          <div className="text-gray-300">Clearance Analysis:</div>
          <div className="space-y-1 mt-1">
            <div className="flex justify-between">
              <span className="text-gray-400">Ground clearance:</span>
              <span className="text-white">{clearance}m</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Rear height:</span>
              <span className="text-white">{specs.rearHeight}m</span>
            </div>
          </div>
        </div>
        <div className="bg-gray-700/50 p-3 rounded-lg">
          <div className="text-gray-300">Curve Analysis:</div>
          <div className="space-y-1 mt-1">
            {/* Turning Radius Analysis */}
            <div className="bg-blue-500/10 p-2 rounded border border-blue-500/20">
              <div className="text-blue-400 font-medium mb-1">Required Turning Radius</div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-400">Minimum:</span>
                  <span className="text-white font-mono">
                    {((length * length) / (2 * specs.width)).toFixed(1)}m
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  R = L²/2w where:
                  <div className="pl-2">
                    L = {length}m (length)
                    <br />
                    w = {specs.width}m (width)
                  </div>
                </div>
              </div>
            </div>
            
            {/* Vertical Curve Analysis */}
            <div className="mt-2">
              <div className="bg-gray-800/50 p-2 rounded">
                <div className="text-gray-300 font-medium mb-1">Crest Curve</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400">K-factor:</span>
                    <span className="text-white font-mono">
                      {((length * length) / (2 * clearance)).toFixed(1)} m/%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Radius:</span>
                    <span className="text-white font-mono">
                      {((length * length) / (8 * clearance)).toFixed(1)} m
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-800/50 p-2 rounded mt-2">
                <div className="text-gray-300 font-medium mb-1">Sag Curve</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-400">K-factor:</span>
                    <span className="text-white font-mono">
                      {((axleSpacing * axleSpacing) / (2 * clearance)).toFixed(1)} m/%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Radius:</span>
                    <span className="text-white font-mono">
                      {((axleSpacing * axleSpacing) / (8 * clearance)).toFixed(1)} m
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};